import { FC, useMemo } from 'react';
import { Box, Card, CardContent, CardHeader, styled, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { ChartData, ChartOptions } from 'chart.js';
import { isUndefined } from 'lodash';
import { useSelector } from 'react-redux';
import { LoadingSection } from 'components';
import { LineBarChart } from 'dashboard409a/components';
import { SubscriptionExpiredBox } from 'dashboard409a/components/SubscriptionExpiredBox';
import { IValuationState } from 'dashboard409a/states/features/valuation';
import { RootStateType } from 'dashboard409a/states/store';
import { formatMD } from 'dashboard409a/utilities';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import { ReportsCardHeader } from '../ReportsCardHeader';

const LINE_COLOR = '#037DE8';

const StyledGrid = styled(Grid)(() => ({
  '& .MuiCard-root': {
    marginBottom: '2%',
    boxShadow: 'none',
  },
}));

const OPTIONS: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  // Hovering anywhere near a date shows its tooltip — exact-pixel hover
  // (the default) feels broken on a sparse line with small dots.
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#FFFFFF',
      titleColor: '#1F2937',
      titleFont: { weight: 'bold', size: 13 },
      bodyColor: '#4B5563',
      borderColor: 'rgba(0, 0, 0, 0.08)',
      borderWidth: 1,
      cornerRadius: 8,
      padding: 10,
      displayColors: false,
      callbacks: {
        label: context => formatCurrency({ value: context.parsed.y, maximumFractionDigits: 2 }),
      },
    },
  },
  scales: {
    x: {
      border: { display: false },
      grid: { display: false },
      ticks: { color: '#9CA3AF' },
    },
    y: {
      border: { display: false, dash: [4, 4] },
      grid: { color: '#E5E7EB' },
      ticks: {
        color: '#9CA3AF',
        maxTicksLimit: 6,
        callback: value => formatCurrency({ value: Number(value), maximumFractionDigits: 2 }),
      },
    },
  },
};

// One point per historical valuation: the marketable value per common share
// at each measurement date. A private company has no daily stock value, so
// the series is exactly as dense as the valuation history.
const CommonStockValueChart: FC = () => {
  const { currentCompany } = useSelector((state: RootStateType) => state.global);
  const { loading, allValuationScalarOpinion } = useSelector<RootStateType, IValuationState>(state => state.valuation);

  const subscriptionIsActive = currentCompany?.is_active;
  const displayExpired = !isUndefined(subscriptionIsActive) && subscriptionIsActive === false;

  const { chartData, hasValues } = useMemo(() => {
    const withValues = [...(allValuationScalarOpinion ?? [])]
      .filter(item => item.non_marketable_value_per_common_share !== null)
      .sort(
        (a, b) =>
          new Date(a.report_date.measurement_date.date).getTime() -
          new Date(b.report_date.measurement_date.date).getTime()
      );

    const data: ChartData<'line'> = {
      labels: withValues.map(item => formatMD({ date: item.report_date.measurement_date.date, format: 'MM/DD/YY' })),
      datasets: [
        {
          data: withValues.map(item => +(item.non_marketable_value_per_common_share as string)),
          borderColor: LINE_COLOR,
          backgroundColor: LINE_COLOR,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
        },
      ],
    };
    return { chartData: data, hasValues: withValues.length > 0 };
  }, [allValuationScalarOpinion]);

  const renderChart = () => {
    if (loading) {
      return <LoadingSection />;
    }
    if (!hasValues) {
      return (
        <Typography color="text.secondary" variant="body2">
          No common stock value recorded for this company&apos;s valuations yet.
        </Typography>
      );
    }
    return <LineBarChart type="line" data={chartData} options={OPTIONS} />;
  };

  return (
    <StyledGrid size={{ xs: 12, lg: 6 }}>
      <Card>
        <CardHeader title={<ReportsCardHeader title="Common Stock Value" />} disableTypography />
        <CardContent>
          {displayExpired ? <SubscriptionExpiredBox /> : <Box sx={{ height: 320 }}>{renderChart()}</Box>}
        </CardContent>
      </Card>
    </StyledGrid>
  );
};

export default CommonStockValueChart;
